/**
 * SET PRICE — the Quote module's heart (MOCKUP_2 · 2026-06-15).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE ONE IDEA (owner-locked): "Whatever price you agree IS what the client
 *  pays." The agreed price set HERE is the single source of truth — on
 *  Convert it seeds `projects/{pid}.gateAmounts` (via `seedGateAmounts` in
 *  `convertLeadToProject`), which the ONE canonical `resolveGateAmount` reads
 *  everywhere (money summary, project page, Record-a-payment modal). So the
 *  front-door price and the money gate can never disagree (the Tarveen $40
 *  fix at the root).
 *
 *  THE 90% FLOW (spec): pick the service → the standard price auto-fills →
 *  change "Agreed price" only if you negotiated something different → answer
 *  GST (routes the billing firm) → Save. One calm screen, NOT a wizard.
 *
 *  ONE PLACE: the price is set from the lead, here. The "Quotes" list is the
 *  RECORD (view/track + edit), never a second create path. The agreed price
 *  is ALSO editable later on a live engagement from the Payment tab
 *  (AgreedPricePanel) — same single value.
 *
 *  PURE LOGIC in `lib/leadQuote.ts` (standardPriceFor / applyLeadPrice /
 *  leadChecklist); this is the thin UI. Persistence is the caller's `onChange`
 *  (the lead `updateLead` write — the agreed-price fields round-trip inside
 *  the notes_internal snapshot, no Firestore rules change).
 *
 *  UI = the ONE locked AVBP system: pure white, slate #2f5d8a accent, no serif/
 *  terracotta. Tokens via var(--…), never literal hex (except the slate accent
 *  on the agreed-price emphasis, matching the mockup identity).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { Box, Btn3 } from './ui';
import { money } from '../lib/moneyModel';
import { getFirm, type Currency } from '../lib/firms';
import type { Lead } from '../lib/leads';
import {
  applyLeadPrice,
  // Canonical Set-4 service names (owner fix 2026-07-03) — the rate card is
  // the naming SSOT; the picker must match the quote lines + rate card.
  canonicalTierOptions,
  completionMessage,
  leadChecklist,
  leadPriceOf,
  standardPriceFor,
} from '../lib/leadQuote';

const SLATE = '#2f5d8a'; // the locked AVBP accent (mockup agreed-price identity)

const fieldStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: '#fff',
  width: '100%',
  boxSizing: 'border-box',
};

export default function SetPrice({
  lead,
  onSave,
  onSyncConvertedProject,
  openSentQuote = null,
}: {
  lead: Lead;
  /** Persist the priced lead (the caller wires this to `updateLead`). */
  onSave: (next: Lead) => void;
  /**
   * BUG B FIX (2026-06-16) — when this lead is ALREADY converted to a project,
   * a price edit here must ALSO flow to that project's agreed price so it
   * appends to the SAME change-history log (`feeHistory`) the Payment-tab
   * editor writes — one shared log, single source. The caller (Leads.tsx)
   * wires this to `setProjectAgreedPrice`; absent / unconverted → lead-only.
   * `prior` carries the lead's pre-edit agreed snapshot so the was→now row is
   * correct and the no-op guard can fire.
   */
  onSyncConvertedProject?: (input: {
    tier: string;
    currency: Currency;
    value: number;
    prior: { currency: Currency | null; value: number | null };
    gst: boolean;
  }) => void;
  /**
   * BREAK-3 (linkage audit 2026-07-03) — the lead's OPEN SENT quote, when one
   * exists (the host fetches via quoteStore.findOpenSentQuoteForLead). While a
   * sent quote is open, hand-editing the agreed price here is LOCKED (mirrors
   * convertGate's open-quote guard): accepting that quote runs
   * priceLeadFromQuote, which silently overwrites whatever is typed here —
   * the drift the audit flagged. The panel shows the reconciliation note
   * ("accepting quote X sets the agreed price to Y") instead of a dead end:
   * resolve through the quote — Accept it, or void it (supersede/expire).
   */
  openSentQuote?: { id: string; total: number; currency: Currency } | null;
}) {
  const snap = leadPriceOf(lead);

  // Form state — defaults to the lead's current agreed snapshot.
  const [tier, setTier] = useState<string>(lead.assignedTier ?? '');
  const [currency, setCurrency] = useState<Currency>(snap.currency);
  const [valStr, setValStr] = useState<string>(
    snap.value != null ? String(snap.value) : '',
  );
  const [gst, setGst] = useState<boolean>(snap.gst);
  const [noCharge, setNoCharge] = useState<boolean>(snap.noCharge);
  const [noChargeReason, setNoChargeReason] = useState<string>(snap.noChargeReason);
  const [saving, setSaving] = useState(false);

  const std = useMemo(() => (tier ? standardPriceFor(tier) : null), [tier]);
  const stdAmount = std ? (currency === 'USD' ? std.rUSD : std.rINR) : null;
  const value = Math.round(Number(valStr.replace(/[^0-9.]/g, '')) || 0);

  // The firm auto-routes from {currency, gst} — USD/GST → Aayatan Veda,
  // INR-no-GST → RKH HUF. We preview it the way applyLeadPrice will route it.
  const previewLead = applyLeadPrice(
    lead,
    { tier, currency, value, gst, noCharge, noChargeReason },
    lead.updatedAt,
  );
  const firm = getFirm(previewLead.firmId ?? 'firm1');

  // The 6 must-haves on the lead AS IT WOULD BE after this save (so the
  // staffer sees what's still missing to send / convert).
  const checklist = leadChecklist(previewLead);
  const gateMsg = completionMessage(previewLead);

  const changedBelowStandard =
    !noCharge && stdAmount != null && value > 0 && value !== stdAmount;

  function save() {
    if (!tier) return;
    if (!noCharge && value <= 0) return;
    setSaving(true);
    try {
      onSave(
        applyLeadPrice(
          lead,
          { tier, currency, value, gst, noCharge, noChargeReason },
          new Date().toISOString(),
        ),
      );
      // BUG B — if this lead is already converted, propagate the agreed price
      // to its project so the change lands in the ONE shared `feeHistory` log
      // (the Payment-tab editor writes there too). `snap` is the lead's
      // pre-edit agreed price → the correct was→now (and the no-op guard).
      if (!noCharge && value > 0 && onSyncConvertedProject) {
        onSyncConvertedProject({
          tier,
          currency,
          value,
          prior: { currency: snap.value != null ? snap.currency : null, value: snap.value },
          gst,
        });
      }
    } finally {
      setSaving(false);
    }
  }

  // BREAK-3 — while a SENT quote is open, the quote owns the price: lock the
  // hand-edit (accepting would silently overwrite it — priceLeadFromQuote).
  const quoteOwnsPrice = !!openSentQuote;
  const quoteDiffers =
    !!openSentQuote && value > 0 && openSentQuote.total !== value;
  const canSave = !!tier && (noCharge || value > 0) && !quoteOwnsPrice;

  return (
    <Box
      title="Set the price for this lead"
      note='The standard price fills in automatically. Change "Agreed price" only if you negotiated something different.'
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Service */}
        <div>
          <label style={lbl}>Service they want</label>
          <select
            aria-label="Service"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            style={fieldStyle}
          >
            <option value="">— pick a service level —</option>
            {canonicalTierOptions().map((o) => (
              <option key={o.tier} value={o.tier}>
                {o.tier} · {o.displayName}
              </option>
            ))}
          </select>
        </div>

        {!noCharge && (
          <>
            {/* Standard price (read-only, from the rate card) */}
            {std && (
              <div style={stdBox}>
                <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
                  Standard price — from the rate card
                </span>
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {std.isFormula ? 'by formula (L3B)' : money(stdAmount ?? 0, currency)}
                </span>
              </div>
            )}

            {/* Agreed price (the editable figure — what's actually due) */}
            <div>
              <label style={{ ...lbl, color: SLATE, fontWeight: 700 }}>
                ● Agreed price — what the client actually pays
              </label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap' }}>
                <select
                  aria-label="Agreed price currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as Currency)}
                  style={{ ...fieldStyle, width: 120 }}
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                </select>
                <div style={{ ...amtWrap, flex: 1, minWidth: 160 }}>
                  <span style={{ color: SLATE, fontWeight: 800, fontSize: 18 }}>
                    {currency === 'USD' ? '$' : '₹'}
                  </span>
                  <input
                    aria-label="Agreed price amount"
                    value={valStr}
                    onChange={(e) => setValStr(e.target.value)}
                    inputMode="decimal"
                    placeholder={stdAmount != null ? String(stdAmount) : '0'}
                    style={amtInput}
                  />
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 6 }}>
                {changedBelowStandard
                  ? 'You changed the agreed price — that’s fine. It’s the agreed figure the client pays.'
                  : 'Same as standard. Type a different number if you agreed a special price.'}
              </div>
            </div>
          </>
        )}

        {/* GST → firm routing */}
        {!noCharge && (
          <div>
            <label style={lbl}>Will they need a GST invoice?</label>
            <div style={seg}>
              <button
                type="button"
                style={{ ...segBtn, ...(gst ? segOn : null) }}
                onClick={() => setGst(true)}
              >
                Yes — GST invoice
              </button>
              <button
                type="button"
                style={{ ...segBtn, ...(!gst ? segOn : null) }}
                onClick={() => setGst(false)}
              >
                No GST invoice needed
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 6 }}>
              Bills from <b style={{ color: 'var(--ink)' }}>{firm.shortName}</b>{' '}
              — auto-chosen from the GST answer.
            </div>
          </div>
        )}

        {/* No charge */}
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={noCharge}
              onChange={(e) => setNoCharge(e.target.checked)}
            />
            No charge (family / referral / goodwill)
          </label>
          {noCharge && (
            <input
              aria-label="No charge reason"
              value={noChargeReason}
              onChange={(e) => setNoChargeReason(e.target.value)}
              placeholder="One-line reason (e.g. family)"
              style={{ ...fieldStyle, marginTop: 8 }}
            />
          )}
        </div>

        {/* Can't-skip-what's-needed — the 6 must-haves */}
        <div style={checkBox}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 8 }}>
            Before this quote goes out / the lead converts
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {checklist.map((c) => (
              <span
                key={c.key}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 'var(--r-pill)',
                  background: c.ok ? 'var(--good-wash)' : 'var(--warn-wash)',
                  color: c.ok ? 'var(--good)' : 'var(--warn)',
                }}
                title={c.ok ? c.label : c.hint}
              >
                {c.ok ? '✓' : '•'} {c.label}
              </span>
            ))}
          </div>
          {gateMsg && (
            <div style={{ fontSize: 12.5, color: 'var(--warn)', marginTop: 8 }}>
              {gateMsg}
            </div>
          )}
        </div>

        {/* Save */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn3 intent="ink" onClick={save} disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Save price'}
          </Btn3>
          {openSentQuote && (
            <span
              style={{ fontSize: 12, color: 'var(--warn-ink, #92400E)', flexBasis: '100%' }}
              data-testid="setprice-open-quote-note"
              role="status"
            >
              Quote <b>{openSentQuote.id}</b> is out with the client — accepting
              it will set the agreed price to{' '}
              <b>{money(openSentQuote.total, openSentQuote.currency)}</b>
              {quoteDiffers ? ` (overwriting the ${money(value, currency)} typed here)` : ''}.
              Hand edits are locked while it's open: Accept the quote, or void
              it (supersede / expire) in Quotes first.
            </span>
          )}
          <span style={{ fontSize: 12, color: 'var(--ink-mute)' }} data-testid="setprice-summary">
            {noCharge
              ? 'No charge — saved on the lead.'
              : value > 0
                ? `Saved as the agreed price · ${money(value, currency)} becomes the amount due`
                : 'Type an agreed price to save.'}
          </span>
        </div>
      </div>
    </Box>
  );
}

const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--ink-soft)',
  marginBottom: 6,
};
const stdBox: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 13px',
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  background: 'var(--inset)',
};
const amtWrap: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: `1px solid ${SLATE}`,
  borderRadius: 'var(--r-sm)',
  padding: '6px 12px',
  background: '#fff',
};
const amtInput: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: 20,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  width: '100%',
  background: 'transparent',
};
const seg: React.CSSProperties = {
  display: 'flex',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  overflow: 'hidden',
};
const segBtn: React.CSSProperties = {
  flex: 1,
  padding: '10px 8px',
  fontSize: 13,
  fontWeight: 600,
  background: '#fff',
  border: 'none',
  borderRight: '1px solid var(--rule)',
  color: 'var(--ink-soft)',
  cursor: 'pointer',
};
const segOn: React.CSSProperties = { background: SLATE, color: '#fff' };
const checkBox: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '12px 14px',
  background: 'var(--inset)',
};
